import axios from 'axios';

/**
 * A stable, UI-branchable classification of a data-room API failure. The `kind` drives which
 * localized message the UI shows (copy lives in the i18n bundles, keyed by kind — never hardcoded
 * English here), so an applicant sees a kind, plain-language explanation, never a raw stack.
 */
export type ErrorKind =
  | 'GONE' // 410 — invite already used / session gone
  | 'TOO_LARGE' // 413 — file too big
  | 'BAD_TYPE' // 415 — unsupported file type
  | 'UNAUTHORIZED' // 401 — session expired / invalid
  | 'RATE_LIMIT' // 429
  | 'CONFLICT' // 409 — the task changed (e.g. a declaration's text version moved on)
  | 'INVALID' // 422 — the submission breaks the task's rules (server message is authoritative)
  | 'NOT_AVAILABLE' // 404 / 501 — endpoint not wired yet
  | 'NETWORK' // no response
  | 'SERVER' // 5xx
  | 'UNKNOWN';

export interface NormalizedError {
  kind: ErrorKind;
  /** HTTP status when known (0 for network/unknown). */
  httpStatus: number;
  /** A server-supplied code, if the body carried one — for logs/diagnostics, not for display. */
  code?: string;
}

export function normalizeError(err: unknown): NormalizedError {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status ?? 0;
    const body = err.response?.data as { code?: string } | undefined;
    const code = body && typeof body === 'object' ? body.code : undefined;
    return { kind: kindForStatus(status), httpStatus: status, code };
  }
  return { kind: 'UNKNOWN', httpStatus: 0 };
}

function kindForStatus(status: number): ErrorKind {
  switch (status) {
    case 0:
      return 'NETWORK';
    case 401:
      return 'UNAUTHORIZED';
    case 404:
    case 501:
      return 'NOT_AVAILABLE';
    case 410:
      return 'GONE';
    case 413:
      return 'TOO_LARGE';
    case 415:
      return 'BAD_TYPE';
    case 409:
      return 'CONFLICT';
    case 422:
      return 'INVALID';
    case 429:
      return 'RATE_LIMIT';
    default:
      if (status >= 500) return 'SERVER';
      return 'UNKNOWN';
  }
}

/** True when the failure means "the invite/session can no longer be used" (terminal). */
export function isGone(e: NormalizedError): boolean {
  return e.kind === 'GONE';
}
